import React, {useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {ProductAPIDataSource} from '../api';
import {ProductType} from '../models/productType.model';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import Button from '../../../Presentation/components/ui/Button';
import Modal from '../../../Presentation/components/ui/Modal';
import TypeForm from './TypeForm';
import {useNotification} from '../../../Services/useNotification';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

const TypeList: React.FC = () => {
  const queryClient = useQueryClient();
  const {success: showSuccess} = useNotification();
  const {data: types = [], isLoading, error} = useQuery({queryKey: ['product-types'], queryFn: ProductAPIDataSource.getProductTypes});

  // حالة المودال
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedType, setSelectedType] = useState<ProductType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // إضافة نوع
  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) => ProductAPIDataSource.createProductType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['product-types']});
      setModalOpen(false);
      showSuccess('تمت إضافة النوع بنجاح');
    },
    onError: (error: Error) => {
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  });

  // تعديل نوع
  const updateMutation = useMutation({
    mutationFn: ({id, name, description}: { id: number; name: string; description?: string }) => ProductAPIDataSource.updateProductType(id, {name, description}),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['product-types']});
      setModalOpen(false);
      setSelectedType(null);
      showSuccess('تم تعديل النوع بنجاح');
    },
    onError: (error: Error) => {
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  });

  // حذف نوع
  const deleteMutation = useMutation({
    mutationFn: (id: number) => ProductAPIDataSource.deleteProductType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['product-types']});
      setDeleteId(null);
      showSuccess('تم حذف النوع بنجاح');
    },
    onError: (error: Error) => {
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  });

  const handleAdd = (data: { name: string; description?: string }) => {
    setSubmitting(true);
    createMutation.mutate(data, {
      onSettled: () => setSubmitting(false)
    });
  };

  const handleEdit = (data: { name: string; description?: string }) => {
    if (!selectedType) {return;}
    setSubmitting(true);
    updateMutation.mutate({id: selectedType.id, ...data}, {
      onSettled: () => setSubmitting(false)
    });
  };

  const handleDelete = (id: number) => {
    setDeleteLoading(true);
    deleteMutation.mutate(id, {
      onSettled: () => setDeleteLoading(false)
    });
  };

  const columns: TableColumn<ProductType>[] = [
    {key: 'id', title: 'ID', render: (row) => row.id},
    {key: 'name', title: 'اسم النوع', render: (row) => row.name},
    {key: 'description', title: 'الوصف', render: (row) => row.description || '-'}
  ];

  if (error) {
    return <div className="text-red-600 text-center py-8">حدث خطأ أثناء جلب الأنواع</div>;
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setModalMode('add'); setSelectedType(null); setModalOpen(true); }}>
          إضافة نوع جديد
        </Button>
      </div>
      <AppTable
        columns={columns}
        data={types}
        rowKey={(row, index) => `type-${row.id}-${index || 0}`}
        emptyText={isLoading ? 'جاري التحميل...' : 'لا توجد أنواع'}
        actions={row => [
          {label: 'تعديل', onClick: () => { setModalMode('edit'); setSelectedType(row); setModalOpen(true); }},
          {label: 'حذف', onClick: () => setDeleteId(row.id), danger: true}
        ]}
        hidePagination={true}
      />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={modalMode === 'add' ? 'إضافة نوع جديد' : 'تعديل النوع'}>
        <TypeForm
          key={modalMode === 'edit' && selectedType ? `edit-${selectedType.id}` : 'add'}
          initialName={modalMode === 'edit' && selectedType ? selectedType.name : ''}
          initialDescription={modalMode === 'edit' && selectedType ? selectedType.description : ''}
          onSubmit={modalMode === 'add' ? handleAdd : handleEdit}
          onCancel={() => setModalOpen(false)}
          submitting={submitting}
        />
      </Modal>
      <Modal open={deleteId !== null} onClose={() => setDeleteId(null)} title="تأكيد الحذف">
        <div className="text-center mb-6">
          هل أنت متأكد أنك تريد حذف هذا النوع؟
        </div>
        <div className="flex gap-4 justify-center">
          <Button variant="ghost" onClick={() => setDeleteId(null)} disabled={deleteLoading}>
            إلغاء
          </Button>
          <Button variant="danger" onClick={() => handleDelete(deleteId!)} isLoading={deleteLoading}>
            حذف
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default TypeList;
